import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { ArrowLeft, Mic, Square, Copy, Download, Sparkles, RefreshCw, PenLine, Wand2 } from "lucide-react";
import api from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { mediaUrl } from "@/lib/media";
import MediaPicker from "@/components/MediaPicker";
import CarouselViewer from "@/components/CarouselViewer";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

const OBJECTIVES = [
  { id: "me", emoji: "❤️", k: "obj_me" },
  { id: "share", emoji: "💬", k: "obj_share" },
  { id: "promote", emoji: "📣", k: "obj_promote" },
  { id: "ad", emoji: "🛍️", k: "obj_ad" },
  { id: "money", emoji: "💰", k: "obj_money" },
  { id: "local", emoji: "📍", k: "obj_local" },
  { id: "guide", emoji: "🤷", k: "obj_guide" },
];
const FORMATS = [
  { id: "reel", emoji: "🎬", k: "fmt_reel" },
  { id: "story", emoji: "📱", k: "fmt_story" },
  { id: "post", emoji: "🖼️", k: "fmt_post" },
  { id: "carousel", emoji: "📚", k: "fmt_carousel" },
];

const Card = ({ onClick, active, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`text-left p-6 rounded-3xl border-2 bg-white transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:shadow-md min-h-[112px] w-full ${active ? "border-primary bg-primary/10 shadow-md" : "border-stone-200"}`}>
    {children}
  </button>
);

const Step = ({ children }) => (
  <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.35 }} className="bt-rise">{children}</motion.div>
);

export default function Create() {
  const { t, lang } = useLang();
  const { user, setUser, refresh } = useAuth();
  const nav = useNavigate();

  const [step, setStep] = useState("objective");
  const [objective, setObjective] = useState(null);
  const [subject, setSubject] = useState("");
  const [audience, setAudience] = useState("");
  const [ideaText, setIdeaText] = useState("");
  const [ideas, setIdeas] = useState([]);
  const [format, setFormat] = useState(null);
  const [concepts, setConcepts] = useState([]);
  const [creation, setCreation] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const handleErr = (e) => {
    if (e.response?.status === 402) toast.error(t("err_credits"));
    else toast.error(t("err_generic"));
  };
  const syncCredits = (credits) => { if (credits != null && user) setUser({ ...user, credits }); };

  const objLabel = () => (OBJECTIVES.find((o) => o.id === objective) ? t(OBJECTIVES.find((o) => o.id === objective).k) : "");

  const chooseObjective = (id) => {
    setObjective(id);
    if (id === "money") { nav("/earn"); return; }
    setStep("media");
  };

  const askIdeas = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/generate/ideas", { objective: objLabel(), subject, audience });
      setIdeas(data.ideas || []);
      syncCredits(data.credits);
      setStep("pickidea");
    } catch (e) { handleErr(e); } finally { setBusy(false); }
  };

  const pickIdea = (text) => { setIdeaText(text); setStep("format"); };

  const chooseFormat = async (id) => {
    setFormat(id);
    setBusy(true);
    setStep("concepts");
    try {
      const { data } = await api.post("/generate/concepts", { idea: ideaText, format: t(FORMATS.find((f) => f.id === id).k) });
      setConcepts(data.concepts || []);
      syncCredits(data.credits);
    } catch (e) { handleErr(e); setStep("format"); } finally { setBusy(false); }
  };

  const doGenerate = async (useAi, conceptName) => {
    setStep("result");
    setBusy(true);
    try {
      const fmt = FORMATS.find((f) => f.id === format);
      const { data } = await api.post("/generate/content", {
        idea: ideaText, format: t(fmt.k), format_id: format,
        concept: conceptName, media_ids: selectedMedia, use_ai_image: !!useAi,
      });
      setCreation(data.creation);
      syncCredits(data.credits);
      confetti({ particleCount: 140, spread: 80, origin: { y: 0.3 }, colors: ["#7C3AED", "#2563EB", "#A78BFA"] });
    } catch (e) { handleErr(e); setStep(selectedMedia.length ? "concepts" : "imagechoice"); } finally { setBusy(false); }
  };

  const [chosenConcept, setChosenConcept] = useState("");
  const chooseConcept = (conceptName) => {
    setChosenConcept(conceptName);
    if (selectedMedia.length > 0) doGenerate(false, conceptName);
    else setStep("imagechoice");
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((tk) => tk.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "idea.webm");
          const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setIdeaText((prev) => (prev ? prev + " " : "") + (data.text || "").trim());
        } catch (e) { handleErr(e); } finally { setTranscribing(false); }
      };
      mr.start(); setRecording(true);
    } catch (e) { toast.error(lang === "fr" ? "Micro non disponible." : "Microphone not available."); }
  };
  const stopRec = () => { mediaRef.current?.stop(); setRecording(false); };

  const copy = async (text) => { const ok = await copyText(text); ok ? toast.success(t("copied")) : toast.error(t("err_generic")); };
  const reset = () => {
    setStep("objective"); setObjective(null); setSubject(""); setAudience("");
    setIdeaText(""); setIdeas([]); setFormat(null); setConcepts([]); setCreation(null);
    setSelectedMedia([]); setChosenConcept("");
    refresh();
  };

  const Back = ({ to }) => (
    <button onClick={to} data-testid="back-btn" className="inline-flex items-center gap-2 text-stone-500 font-semibold mb-5 hover:text-stone-800">
      <ArrowLeft size={20} /> {t("back")}
    </button>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 min-h-[70vh]">
      <AnimatePresence mode="wait">
        {step === "objective" && (
          <Step key="objective">
            <h1 className="font-head font-black text-3xl sm:text-4xl text-center mb-2">👋 {t("today_q")}</h1>
            <p className="text-center text-stone-500 mb-8">{user?.first_name}</p>
            <div className="grid sm:grid-cols-2 gap-5">
              {OBJECTIVES.map((o) => (
                <Card key={o.id} onClick={() => chooseObjective(o.id)} testid={`objective-${o.id}`}>
                  <div className="text-4xl mb-3">{o.emoji}</div>
                  <div className="font-head font-bold text-xl">{t(o.k)}</div>
                </Card>
              ))}
            </div>
          </Step>
        )}

        {step === "media" && (
          <Step key="media">
            <Back to={() => setStep("objective")} />
            <h1 className="font-head font-black text-3xl sm:text-4xl text-center mb-2">📸 {t("media_step_title")}</h1>
            <p className="text-center text-stone-500 mb-8 max-w-xl mx-auto">{t("media_step_sub")}</p>
            <div className="max-w-2xl mx-auto">
              <MediaPicker selectable selected={selectedMedia} onChange={setSelectedMedia} />
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button onClick={() => setStep("ideaq")} disabled={selectedMedia.length === 0} data-testid="media-continue"
                  className="flex-1 rounded-full bg-primary hover:bg-[#6D28D9] min-h-[56px] font-bold text-lg">
                  {t("media_continue")} {selectedMedia.length > 0 ? `(${selectedMedia.length})` : ""}
                </Button>
                <Button variant="outline" onClick={() => { setSelectedMedia([]); setStep("ideaq"); }} data-testid="media-skip"
                  className="rounded-full border-2 min-h-[56px] font-bold text-lg">
                  {t("media_skip")}
                </Button>
              </div>
            </div>
          </Step>
        )}

        {step === "ideaq" && (
          <Step key="ideaq">
            <Back to={() => setStep("media")} />
            <h1 className="font-head font-black text-3xl sm:text-4xl text-center mb-10">{t("idea_q")}</h1>
            <div className="grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto">
              <Card onClick={() => setStep("myidea")} testid="idea-yes">
                <div className="text-4xl mb-3">💡</div>
                <div className="font-head font-bold text-xl">{t("idea_yes")}</div>
              </Card>
              <Card onClick={() => setStep("noidea")} testid="idea-no">
                <div className="text-4xl mb-3">✨</div>
                <div className="font-head font-bold text-xl">{t("idea_no")}</div>
              </Card>
            </div>
          </Step>
        )}

        {step === "myidea" && (
          <Step key="myidea">
            <Back to={() => setStep("ideaq")} />
            <h1 className="font-head font-black text-3xl text-center mb-8 flex items-center justify-center gap-2"><PenLine /> {t("idea_type")}</h1>
            <Textarea value={ideaText} onChange={(e) => setIdeaText(e.target.value)} data-testid="idea-textarea"
              placeholder={t("idea_type")} className="min-h-[160px] rounded-2xl border-2 border-stone-300 text-lg p-5 focus-visible:ring-4 focus-visible:ring-primary/30" />
            <div className="flex flex-col items-center my-8">
              <p className="text-stone-500 font-semibold mb-4">🎙️ {t("idea_voice")}</p>
              <button onClick={recording ? stopRec : startRec} disabled={transcribing} data-testid="voice-btn"
                className={`w-24 h-24 rounded-full grid place-items-center text-white shadow-lg transition-colors ${recording ? "bg-red-500 bt-recording" : "bg-primary hover:bg-[#6D28D9]"}`}>
                {recording ? <Square size={34} /> : <Mic size={38} />}
              </button>
              <p className="mt-4 text-stone-500 h-5">{transcribing ? t("listening") : recording ? t("recording") : ""}</p>
            </div>
            <Button onClick={() => setStep("format")} disabled={!ideaText.trim()} data-testid="idea-continue"
              className="w-full rounded-full bg-primary hover:bg-[#6D28D9] min-h-[60px] text-lg font-bold">
              {t("next")}
            </Button>
          </Step>
        )}

        {step === "noidea" && (
          <Step key="noidea">
            <Back to={() => setStep("ideaq")} />
            <div className="max-w-xl mx-auto space-y-6">
              <div>
                <h2 className="font-head font-bold text-2xl mb-3">{t("subject_q")}</h2>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("subject_ph")} data-testid="input-subject"
                  className="min-h-[56px] rounded-xl border-2 border-stone-300 text-lg px-4" />
              </div>
              <div>
                <h2 className="font-head font-bold text-2xl mb-3">{t("audience_q")}</h2>
                <Input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder={t("audience_ph")} data-testid="input-audience"
                  className="min-h-[56px] rounded-xl border-2 border-stone-300 text-lg px-4" />
              </div>
              <Button onClick={askIdeas} disabled={busy || !subject.trim()} data-testid="get-ideas-btn"
                className="w-full rounded-full bg-primary hover:bg-[#6D28D9] min-h-[60px] text-lg font-bold">
                {busy ? t("loading") : <><Sparkles className="mr-2" size={20} /> {t("get_ideas")}</>}
              </Button>
            </div>
          </Step>
        )}

        {step === "pickidea" && (
          <Step key="pickidea">
            <Back to={() => setStep("noidea")} />
            <h1 className="font-head font-black text-3xl text-center mb-8">{t("pick_idea")}</h1>
            <div className="grid gap-4 max-w-2xl mx-auto">
              {ideas.map((idea, i) => (
                <Card key={i} onClick={() => pickIdea(`${idea.title}. ${idea.description}`)} testid={`idea-option-${i}`}>
                  <div className="font-head font-bold text-xl mb-1">{idea.title}</div>
                  <div className="text-stone-600">{idea.description}</div>
                </Card>
              ))}
            </div>
          </Step>
        )}

        {step === "format" && (
          <Step key="format">
            <Back to={() => setStep(ideas.length ? "pickidea" : "myidea")} />
            <h1 className="font-head font-black text-3xl sm:text-4xl text-center mb-8">{t("format_q")}</h1>
            <div className="grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto">
              {FORMATS.map((f) => (
                <Card key={f.id} onClick={() => chooseFormat(f.id)} active={format === f.id} testid={`format-${f.id}`}>
                  <div className="text-4xl mb-3">{f.emoji}</div>
                  <div className="font-head font-bold text-xl">{t(f.k)}</div>
                  <div className="text-stone-600 mt-1">{t(f.k + "_d")}</div>
                </Card>
              ))}
              <Card onClick={() => chooseFormat("post")} testid="format-guide">
                <div className="text-4xl mb-3">🤷</div>
                <div className="font-head font-bold text-xl">{t("dont_know")}</div>
              </Card>
            </div>
          </Step>
        )}

        {step === "concepts" && (
          <Step key="concepts">
            <Back to={() => setStep("format")} />
            <h1 className="font-head font-black text-3xl text-center mb-8">{t("concept_q")}</h1>
            {busy ? (
              <div className="text-center py-16 text-stone-500 text-lg animate-pulse">{t("loading")}</div>
            ) : (
              <div className="grid gap-4 max-w-2xl mx-auto">
                {concepts.map((c, i) => (
                  <Card key={i} onClick={() => chooseConcept(c.name)} testid={`concept-${i}`}>
                    <div className="flex items-start gap-3">
                      <span className="text-3xl">{c.emoji || "✨"}</span>
                      <div>
                        <div className="font-head font-bold text-xl">{c.name}</div>
                        <div className="text-stone-600">{c.description}</div>
                      </div>
                    </div>
                  </Card>
                ))}
                <Card onClick={() => chooseConcept(concepts[0]?.name || "simple")} testid="concept-guide">
                  <div className="flex items-center gap-3"><span className="text-3xl">🤷</span>
                    <div className="font-head font-bold text-xl">{t("dont_know")}</div></div>
                </Card>
              </div>
            )}
          </Step>
        )}

        {step === "imagechoice" && (
          <Step key="imagechoice">
            <Back to={() => setStep("concepts")} />
            <h1 className="font-head font-black text-3xl text-center mb-8">{t("ai_image_q")}</h1>
            <div className="grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto">
              <Card onClick={() => setStep("media")} testid="imgchoice-mine">
                <div className="text-4xl mb-3">📸</div>
                <div className="font-head font-bold text-xl">{t("ai_image_mine")}</div>
              </Card>
              <Card onClick={() => doGenerate(true, chosenConcept)} testid="imgchoice-ai">
                <div className="text-4xl mb-3">🎨</div>
                <div className="font-head font-bold text-xl">{t("ai_image_ai")}</div>
              </Card>
              <Card onClick={() => doGenerate(false, chosenConcept)} testid="imgchoice-none">
                <div className="text-4xl mb-3">✍️</div>
                <div className="font-head font-bold text-xl">{t("ai_image_none")}</div>
              </Card>
            </div>
          </Step>
        )}

        {step === "result" && (
          <Step key="result">
            {busy || !creation ? (
              <div className="text-center py-24">
                <div className="text-6xl mb-6 animate-bounce">✨</div>
                <p className="font-head font-bold text-2xl text-stone-700">{t("creating")}</p>
              </div>
            ) : (
              <div>
                <h1 className="font-head font-black text-3xl sm:text-4xl text-center mb-2">{t("result_title")}</h1>
                {creation.uses_user_media && <p className="text-center text-primary font-semibold mb-6">{t("with_own_photos")}</p>}
                {!creation.uses_user_media && <div className="mb-6" />}
                <div className="grid lg:grid-cols-2 gap-6">
                  {creation.slides?.length > 0 ? (
                    <div>
                      <CarouselViewer slides={creation.slides}
                        fallbackImage={creation.image_b64 ? `data:image/png;base64,${creation.image_b64}` : null} />
                    </div>
                  ) : creation.media_ids?.length > 0 ? (
                    <div data-testid="result-user-media">
                      {creation.media_ids.length === 1 ? (
                        <div className="rounded-3xl overflow-hidden border-2 border-stone-200 bg-white aspect-[4/5]">
                          <img src={mediaUrl(creation.media_ids[0])} alt={creation.title} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          {creation.media_ids.map((mid) => (
                            <div key={mid} className="rounded-2xl overflow-hidden border-2 border-stone-200 bg-white aspect-square">
                              <img src={mediaUrl(mid)} alt="" className="w-full h-full object-cover" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : creation.image_b64 ? (
                    <div className="rounded-3xl overflow-hidden border-2 border-stone-200 bg-white">
                      <img src={`data:image/png;base64,${creation.image_b64}`} alt={creation.title} data-testid="result-image" className="w-full object-cover" />
                      <div className="p-4">
                        <Button variant="outline" data-testid="download-image" className="w-full rounded-full border-2 min-h-[48px] font-semibold"
                          onClick={() => { const a = document.createElement("a"); a.href = `data:image/png;base64,${creation.image_b64}`; a.download = "betool.png"; a.click(); }}>
                          <Download size={18} className="mr-2" /> {t("download_img")}
                        </Button>
                      </div>
                    </div>
                  ) : null}
                  <div className="space-y-5">
                    <div className="bg-white rounded-3xl border-2 border-stone-200 p-6" data-testid="result-caption">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-head font-bold text-xl">{t("caption")}</h3>
                        <Button size="sm" variant="ghost" onClick={() => copy(creation.caption)} className="rounded-full font-semibold"><Copy size={16} className="mr-1" />{t("copy")}</Button>
                      </div>
                      <p className="whitespace-pre-wrap text-stone-700 leading-relaxed">{creation.caption}</p>
                      {creation.hashtags?.length > 0 && (
                        <p className="mt-4 text-primary font-semibold">{creation.hashtags.join(" ")}</p>
                      )}
                    </div>
                    {creation.script && (
                      <div className="bg-white rounded-3xl border-2 border-stone-200 p-6" data-testid="result-script">
                        <h3 className="font-head font-bold text-xl mb-2">{t("script")}</h3>
                        <p className="whitespace-pre-wrap text-stone-700 leading-relaxed">{creation.script}</p>
                      </div>
                    )}
                    {creation.tips?.length > 0 && (
                      <div className="bg-secondary/40 rounded-3xl border-2 border-primary/20 p-6">
                        <h3 className="font-head font-bold text-xl mb-2">💡 {t("tips")}</h3>
                        <ul className="list-disc pl-5 space-y-1 text-stone-700">{creation.tips.map((tip, i) => <li key={i}>{tip}</li>)}</ul>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                  <Button onClick={reset} data-testid="create-new" className="rounded-full bg-primary hover:bg-[#6D28D9] min-h-[56px] px-8 font-bold text-lg">
                    <RefreshCw size={18} className="mr-2" /> {t("create_new")}
                  </Button>
                  <Button variant="outline" onClick={() => nav("/studio")} className="rounded-full border-2 min-h-[56px] px-8 font-bold text-lg">
                    <Wand2 size={18} className="mr-2" /> {t("nav_studio")}
                  </Button>
                </div>
              </div>
            )}
          </Step>
        )}
      </AnimatePresence>
    </div>
  );
}
